import React from 'react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Legend } from 'recharts';
import { AnalysisResult } from '../types';

interface PerformanceRadarProps {
  result: AnalysisResult;
}

export const PerformanceRadar: React.FC<PerformanceRadarProps> = ({ result }) => {
  const { performanceRatios, fvProfile, trainingReadiness } = result;
  
  if (!performanceRatios || !trainingReadiness) return null;

  // Normalize values to 0-100 scale for radar display
  const data = [
    {
      metric: 'Force',
      athlete: Math.min(performanceRatios.forceToWeightRatio * 100, 100),
      norm: 75
    },
    {
      metric: 'Velocity',
      athlete: Math.min(performanceRatios.velocityEfficiency * 100, 100),
      norm: 85
    },
    {
      metric: 'Power',
      athlete: Math.min(performanceRatios.powerToWeightRatio / 20 * 100, 100),
      norm: 70
    },
    {
      metric: 'Speed Reserve',
      athlete: Math.min(trainingReadiness.speedReserve * 50, 100),
      norm: 60
    },
    {
      metric: 'Consistency',
      athlete: trainingReadiness.neuromuscularReadiness * 100,
      norm: 95
    },
    {
      metric: 'Acceleration',
      athlete: Math.min(Math.abs(trainingReadiness.accelerationIndex) * 10, 100),
      norm: 80
    }
  ];

  return (
    <div className="bg-theme-surface-1 border border-theme-border rounded-2xl p-6 shadow-card">
      <h3 className="text-2xl font-semibold tracking-tight text-theme-text mb-6">Performance Profile</h3>
      
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={data} margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
            <PolarGrid stroke="#374151" />
            <PolarAngleAxis 
              dataKey="metric" 
              tick={{ fill: '#9CA3AF', fontSize: 12 }}
            />
            <PolarRadiusAxis 
              angle={90} 
              domain={[0, 100]} 
              tick={{ fill: '#9CA3AF', fontSize: 10 }}
              tickCount={5}
            />
            <Radar
              name="Sport Norm"
              dataKey="norm"
              stroke="#6B7280"
              fill="#6B7280"
              fillOpacity={0.1}
              strokeWidth={2}
              strokeDasharray="5 5"
            />
            <Radar
              name="Athlete"
              dataKey="athlete"
              stroke="#60A5FA"
              fill="#60A5FA"
              fillOpacity={0.3}
              strokeWidth={3}
            />
            <Legend 
              wrapperStyle={{ color: '#9CA3AF' }}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>
      
      <div className="mt-4 text-sm text-theme-muted">
        <p>Blue area shows athlete profile vs gray sport norms. Larger blue areas indicate strengths.</p>
      </div>
    </div>
  );
};